import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer, Share2 } from 'lucide-react';
import type { AlterationPayments, OrderPayments, Payment } from '@tailoring-crm/shared';
import { Button, Dialog, DialogContent, DialogFooter, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../../lib/auth/auth-context.js';
import { formatCurrency, formatDateLong, formatMobile } from '../../lib/format.js';

/**
 * The slip a customer leaves with.
 *
 * It is HTML rather than a PDF on purpose. A shop's printer is whatever is plugged into the
 * counter, its phone's share sheet already knows how to put text into WhatsApp, and neither
 * needs a document generator on the server or three hundred kilobytes in the bundle. Print
 * gets a clean sheet through one print stylesheet (`.receipt-sheet` in `index.css`); share
 * gets the same facts as plain text, which is what actually reaches the customer.
 *
 * The balance printed is the balance AS AT THIS ENTRY — the running total up to and including
 * it — not the order's balance today. A receipt handed over on Tuesday must still say what was
 * owed on Tuesday when it resurfaces in an argument in September.
 */

export interface ReceiptDialogProps {
  payment: Payment;
  /** The ledger it is from — an order's, or an alteration's (which has a charge, not a total). */
  payments: OrderPayments | AlterationPayments;
  onClose: () => void;
}

/** What had been paid once this entry landed, from the entries themselves. */
function paidAsAt(payments: OrderPayments | AlterationPayments, payment: Payment): number {
  const index = payments.entries.findIndex((entry) => entry.id === payment.id);
  const upToHere = index >= 0 ? payments.entries.slice(0, index + 1) : payments.entries;
  return upToHere.reduce((sum, entry) => sum + entry.signedAmountPaise, 0);
}

export function ReceiptDialog({ payment, payments, onClose }: ReceiptDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { shop } = useAuth();

  const shopName = shop?.name ?? t('app.name');
  const forOrder = 'orderId' in payments;
  const number = forOrder ? payments.orderNumber : payments.alterationNumber;
  const numberLabel = t(forOrder ? 'payments.receipt.order' : 'payments.receipt.alteration');
  const totalPaise = forOrder ? payments.totalPaise : payments.chargePaise;
  const totalLabel = t(forOrder ? 'orders.total' : 'alterations.field.charge');
  const paidPaise = paidAsAt(payments, payment);
  const balancePaise = Math.max(0, totalPaise - paidPaise);
  const isRefund = payment.signedAmountPaise < 0;

  const asText = [
    shopName,
    t(isRefund ? 'payments.receipt.refundHeading' : 'payments.receipt.heading'),
    `${numberLabel}: ${number}`,
    `${t('payments.receipt.date')}: ${formatDateLong(payment.receivedAt)}`,
    `${t('payments.amount')}: ${formatCurrency(payment.amountPaise)} (${t(
      `payments.methods.${payment.method}`,
    )})`,
    payment.reference ? `${t('payments.reference')}: ${payment.reference}` : null,
    `${t('payments.receipt.paidSoFar')}: ${formatCurrency(paidPaise)}`,
    `${t('payments.receipt.balanceThen')}: ${formatCurrency(balancePaise)}`,
  ]
    .filter((line): line is string => line !== null)
    .join('\n');

  const onShare = async (): Promise<void> => {
    const sharer = navigator as Navigator & {
      share?: (data: { title?: string; text: string }) => Promise<void>;
    };
    try {
      if (typeof sharer.share === 'function') {
        await sharer.share({ title: t('payments.receipt.heading'), text: asText });
        return;
      }
      await navigator.clipboard.writeText(asText);
      toast({ title: t('payments.receipt.copied'), variant: 'success' });
    } catch {
      // Share cancelled, or the clipboard refused on an insecure origin. The receipt is on
      // screen and selectable, so this is a nudge rather than a failure.
      toast({ title: t('payments.receipt.shareFailed') });
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <section
          className="receipt-sheet flex flex-col gap-3 rounded-lg border border-border p-4"
          aria-label={t('payments.receipt.heading')}
        >
          <header className="flex flex-col gap-0.5 border-b border-border pb-3 text-center">
            <h2 className="text-base font-semibold">{shopName}</h2>
            <p className="text-sm text-muted-foreground">
              {t(isRefund ? 'payments.receipt.refundHeading' : 'payments.receipt.heading')}
            </p>
          </header>

          <dl className="flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{numberLabel}</dt>
              <dd className="font-medium">{number}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t('payments.receipt.customer')}</dt>
              <dd className="font-medium">
                {payments.customerName} · {formatMobile(payments.customerPhone)}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t('payments.receipt.date')}</dt>
              <dd className="font-medium">{formatDateLong(payment.receivedAt)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t('payments.method')}</dt>
              <dd className="font-medium">{t(`payments.methods.${payment.method}`)}</dd>
            </div>
            {payment.reference ? (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">{t('payments.reference')}</dt>
                <dd className="font-medium">{payment.reference}</dd>
              </div>
            ) : null}
          </dl>

          <p className="flex items-baseline justify-between gap-3 border-y border-border py-3 text-lg font-semibold">
            <span>{t(isRefund ? 'payments.receipt.refunded' : 'payments.receipt.received')}</span>
            <span className="tabular-nums">{formatCurrency(payment.amountPaise)}</span>
          </p>

          <dl className="flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{totalLabel}</dt>
              <dd className="tabular-nums">{formatCurrency(totalPaise)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t('payments.receipt.paidSoFar')}</dt>
              <dd className="tabular-nums">{formatCurrency(paidPaise)}</dd>
            </div>
            <div className="flex justify-between gap-3 font-semibold">
              <dt>{t('payments.receipt.balanceThen')}</dt>
              <dd className="tabular-nums">{formatCurrency(balancePaise)}</dd>
            </div>
          </dl>

          {payment.recordedByName ? (
            <p className="text-xs text-muted-foreground">
              {t('payments.receipt.recordedBy', { name: payment.recordedByName })}
            </p>
          ) : null}
        </section>

        <DialogFooter className="print-hidden">
          <Button variant="outline" onClick={() => void onShare()}>
            <Share2 aria-hidden="true" />
            {t('payments.receipt.share')}
          </Button>
          <Button onClick={() => window.print()}>
            <Printer aria-hidden="true" />
            {t('payments.receipt.print')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReceiptDialog;
